import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import MetaControl from './index';

export default function ValidatedControl({
  attrName,
  resource,
  onInlineSubmit,
  alwaysDirty,
  ...rest
}) {
  const metaField = resource.meta().field(attrName);
  if (!metaField) {
    throw new Error(`Meta not specified for attribute: ${attrName}`);
  }
  const [rollbackValue, setRollbackValue] = React.useState(
    resource.attr(attrName),
  );

  const [submitState, setSubmitState] = React.useState({
    saving: false,
    displayFailure: false,
    displaySuccess: false,
  });

  const isDirty = () => alwaysDirty || rollbackValue !== resource.attr(attrName);

  React.useEffect(() => {
    if (submitState.displaySuccess || submitState.displayFailure) {
      const timeout = setTimeout(
        () => setSubmitState({
          ...submitState,
          displayFailure: false,
          displaySuccess: false,
        }),
        3500,
      );
      return () => clearTimeout(timeout);
    }
    return () => null;
  }, [submitState.displaySuccess, submitState.displayFailure, setSubmitState]);

  const dispatchSubmit = async (event, presubmitAttribute) => {
    if (Array.isArray(presubmitAttribute)) {
      resource.attr(...presubmitAttribute);
    }

    if (isDirty() && !submitState.saving) {
      setSubmitState({ ...submitState, saving: true });
      try {
        const res = await onInlineSubmit(event);
        if (res instanceof fr.Collection) {
          const [next] = res;
          setRollbackValue(next.attr(attrName));
        }
        setSubmitState({
          displaySuccess: true,
          displayFailure: false,
          saving: false,
        });
      } catch (err) {
        resource.attr(attrName, rollbackValue);
        setSubmitState({
          displaySuccess: false,
          displayFailure: true,
          saving: false,
        });
      }
    }
    return null;
  };

  const handleKeyPress = event => {
    switch (event.key) {
      case 'Enter':
        dispatchSubmit(event);
        break;
      default: {
        break;
      }
    }
  };

  React.useEffect(() => {
    if (resource.attr(attrName) !== rollbackValue) {
      setRollbackValue(resource.attr(attrName));
    }
  }, [resource.resourceInstanceId]);

  const HelperProps = {
    isDirty: isDirty(),
    ...submitState,
  };
  return (
    <MetaControl
      attrName={attrName}
      resource={resource}
      HelperProps={HelperProps}
      onInlineSubmit={dispatchSubmit}
      onKeyDown={handleKeyPress}
      {...rest}
    />
  );
}

ValidatedControl.defaultProps = {
  alwaysDirty: false,
};

ValidatedControl.propTypes = {
  attrName: PropTypes.string.isRequired,
  resource: PropTypes.object.isRequired,
  onInlineSubmit: PropTypes.func.isRequired,
  alwaysDirty: PropTypes.bool,
};
